import {
  Button,
  CreateButton,
  DateField,
  FilterDropdown,
  FilterDropdownProps,
  Form,
  List,
  SaveButton,
  Select,
  Space,
  Table,
  getDefaultSortOrder,
  useDrawerForm,
  useEditableTable,
  useModal,
  useSelect
} from '@pankod/refine-antd'
import { IResourceComponentsProps, useSelect as useSelectCore } from '@pankod/refine-core'
import { AddAmountToWalletModal } from 'components/AddAmountToWallet/AddAmountToWalletModal'
import { CreateUserButton } from 'components/CreateUserButton/CreateUserButton'
import { ButtonLogicDelete } from 'components/DeleteModal/buttonLogicDelete'
import { DeleteModal } from 'components/DeleteModal/modal'
import { ExportToExcelButton } from 'components/ExportToExcelButton/ExportToExcelButton'
import { ExportUsersWithoutOrdersButton } from 'components/ExportUsersWithoutOrdersButton/ExportUserWithoutOrdersButton'
import { DateFilterDropdown, SelectFilterDropdown } from 'components/filterDropdown'
import { MagicLinkButton } from 'components/MagicLinkButton/MagicLinkButton'
import { getInternalShipmentTotal } from 'components/orders/user_addresses'
import { UserOrdersModal } from 'components/orders/userOrdersModal'
import { ReferralBenefitsHistoryModal } from 'components/ReferralBenefitsHistoryModal/ReferralBenefitsHistoryModal'
import { Order, ReferralBenefits, Supplement, User, definitions } from 'interfaces'
import { Key, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ADMITTED_STATUS, BenefitsData, CANCELLED_STATUS, DECREASE_RETURN_STATUS, DEFAULT_DATE_TIME_FORMAT, DELIVERED_STATUS, INTERNAL_SPENDING_RETURN_STATUS, ORDER_STATUS_ID_ON_HOLD, ORDER_STATUS_ID_PENDING_PAYMENT, ORDER_STATUS_ID_REQUESTED, PARCIAL_RETURN_STATUS, PENALIZATION_STATUS, PREPARED_STATUS, PROCESSED_STATUS, REFUND_STATUS, RETURN_STATUS, SHIPPED_STATUS, STATUS_TAG_COLORS, SUPPLEMENT_STATUS, calculateReferralAndWalletAmounts, currency, formatDateTime, getAllUsers, getLastOrder } from 'utility'

export const UserList: React.FC<IResourceComponentsProps> = () => {
  const selectMeta = `id, email, name, role_id, created_at, last_access, wallet_balance, referred_by_user_id(referral_benefits!referrer_user_id(id, benefit_amount)),
    role(name),
    referrer_benefits:referral_benefits!referrer_user_id(id, order_id, status, benefit_amount, created_at, referred_expired_at, referrer_user_id(id, name), referred_user_id(id, name)),
    referred_benefits:referral_benefits!referred_user_id(id, order_id, status, benefit_amount, created_at, referred_expired_at, referrer_user_id(id, name), referred_user_id(id, name), promo_id),
    orders!user_id(id, status_id, status_id_name:status_id(name), marketplace_id(name), alias, wallet_amount_used, total, created_at, user_id(name)),
    addresses!shipping_address(phone_number)`

  const { t } = useTranslation()
  const { modalProps, show, close } = useModal()
  const [id, setId] = useState(0)
  const [filterPhoneNumber, setFilterPhoneNumber] = useState<Key[]|undefined>(undefined)
  const [activeFilter, setActiveFilter] = useState<boolean>(false)

  // Export progress state
  const [exportProgress, setExportProgress] = useState(0)
  const [isExporting, setIsExporting] = useState(false)

  const {
    tableProps,
    sorter,
    formProps,
    isEditing,
    setId: setEditId,
    saveButtonProps,
    cancelButtonProps,
    filters,
    setFilters,
    tableQueryResult
  } = useEditableTable<User>({
    permanentFilter: [
      {
        field: 'deleted',
        operator: 'eq',
        value: false
      }
    ],
    metaData: {
      select: (activeFilter) ? selectMeta.replace('addresses!shipping_address(phone_number)', 'addresses!shipping_address!inner(phone_number)') : selectMeta
    },
    initialSorter: [
      {
        field: 'orders.id',
        order: 'asc'
      }
    ]
  })

  const { options: rolesOptions } = useSelectCore<definitions['roles']>({
    resource: 'roles',
    fetchSize: 30,
    optionLabel: 'name',
    optionValue: 'id',
    sort: [
      {
        field: 'id',
        order: 'asc'
      }
    ]
  })

  useEffect(() => {
    if (filters && filterPhoneNumber) {
      if (filters.find((x: any) => x.field === 'addresses.phone_number')) {
        setActiveFilter(true)
      } else {
        setActiveFilter(false)
      }
    }
  }, [filters])

  const { selectProps: selectPhoneProps } = useSelect({
    resource: 'addresses',
    fetchSize: 30,
    optionValue: 'phone_number',
    optionLabel: 'phone_number',
    metaData: {
      select: 'phone_number'
    },
    filters: [
      {
        field: 'phone_number',
        operator: 'null',
        value: false
      }
    ]
  })

  const {
    drawerProps: createDrawerProps,
    formProps: createFormProps,
    show: createUserShow,
    close: createUserClose
  } = useDrawerForm<definitions['users']>({
    action: 'create',
    resource: 'users',
    redirect: false
  })

  // eslint-disable-next-line camelcase
  const prepareData = (user: User & { orders: Order[], referrer_benefits: ReferralBenefits[], referred_benefits: ReferralBenefits[] }) => {
    const benefitsData: BenefitsData[] = []
    if (user.referred_benefits) {
      user.referred_benefits.forEach((rb: any) => {
        benefitsData.push({
          key: `benefit-referred-${rb.id}`,
          referred: true,
          name: (rb.referrer_user_id.id !== 0) ? rb.referred_user_id.name || '' : rb.referrer_user_id.name || '',
          status: t(`users.referral_benefits.${rb.status}`),
          benefit_amount: rb.benefit_amount,
          created_at: rb.created_at,
          referred_expired_at: rb.referred_expired_at || null,
          tag_color: STATUS_TAG_COLORS[rb.status as keyof typeof STATUS_TAG_COLORS],
          order_id: rb.order_id,
          promo_id: rb.promo_id || null
        })
      })
    }
    if (user.referrer_benefits) {
      user.referrer_benefits.forEach((rb: any) => {
        benefitsData.push({
          key: `benefit-referrer-${rb.id}-${rb.referred_user_id.name}`,
          referred: false,
          name: rb.referred_user_id.name || '',
          status: t(`users.referral_benefits.${rb.status}`),
          benefit_amount: rb.benefit_amount,
          created_at: rb.created_at,
          referred_expired_at: rb.referred_expired_at || null,
          tag_color: STATUS_TAG_COLORS[rb.status as keyof typeof STATUS_TAG_COLORS]
        })
      })
    }
    if (user.orders) {
      user.orders.forEach((order) => {
        if (order.status_id && [ORDER_STATUS_ID_REQUESTED, PROCESSED_STATUS, SUPPLEMENT_STATUS, ADMITTED_STATUS, PREPARED_STATUS, SHIPPED_STATUS, DELIVERED_STATUS, ORDER_STATUS_ID_ON_HOLD, CANCELLED_STATUS, PARCIAL_RETURN_STATUS, PENALIZATION_STATUS, ORDER_STATUS_ID_PENDING_PAYMENT, REFUND_STATUS, RETURN_STATUS].includes(order.status_id)) {
          benefitsData.push({
            key: `order-${order.id}`,
            referred: true,
            name: order.user_id.name || '',
            status: t('users.referral_benefits.used_in_order'),
            benefit_amount: order.wallet_amount_used,
            created_at: order.created_at,
            referred_expired_at: null,
            tag_color: STATUS_TAG_COLORS.default,
            order_id: { id: order.id, total: order.total }
          })
        }
      })
    }

    const sortedBenefits = benefitsData.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    if (sortedBenefits[sortedBenefits.length - 1]?.referred) {
      sortedBenefits[sortedBenefits.length - 1].tag_color = STATUS_TAG_COLORS.accepted
      sortedBenefits[sortedBenefits.length - 1].status = t('users.referral_benefits.accepted')
    }

    const { totalBenefits: _totalBenefits, expiredBenefits: _expiredBenefits } = calculateReferralAndWalletAmounts(benefitsData, user)

    const _totalWalletAmountUsed = benefitsData.filter(benefit => benefit.tag_color === STATUS_TAG_COLORS.default)
      .reduce((acc, curr) => acc + curr.benefit_amount, 0)

    return { accumulatedTotal: _totalBenefits, totalWalletAmountUsed: _totalWalletAmountUsed, expiredBenefits: _expiredBenefits }
  }

  const dataToExport = (userList: any[]) => {
    return userList?.map((user: any) => {
      const internalShipmentTotal = getInternalShipmentTotal(user.orders)
      const supplementsTotal = user.orders?.filter((o: Order) => o.status_id && ![ORDER_STATUS_ID_PENDING_PAYMENT, CANCELLED_STATUS, RETURN_STATUS].includes(o.status_id))
        .map((o: Order) => {
          const sups = o.supplements.filter((s: Supplement) => !s.deleted).filter((s: Supplement) => s.volumetric)
          return sups.reduce((acc, curr) => acc + (curr.supplement || 0), 0)
        })
        .reduce((acc: any, curr: any) => acc + curr, 0)
      const insuranceTotal = user.orders?.filter((o: Order) => o.status_id && ![ORDER_STATUS_ID_PENDING_PAYMENT, CANCELLED_STATUS, RETURN_STATUS].includes(o.status_id)).reduce((acc: any, curr: any) => acc + (curr.shipping_insurance_price || 0), 0) || 0
      const customOrdersTotal = user.orders?.filter((o: Order) => o.status_id && ![ORDER_STATUS_ID_PENDING_PAYMENT, CANCELLED_STATUS, RETURN_STATUS].includes(o.status_id)).reduce((acc: any, curr: any) => acc + curr.custom_order_price, 0) || 0
      const internalTotal = Number(internalShipmentTotal) + Number(supplementsTotal) + Number(insuranceTotal) + Number(customOrdersTotal)

      const sortedOrders = user?.orders.sort((a: any, b: any) => b.id - a.id)

      const { accumulatedTotal, totalWalletAmountUsed, expiredBenefits } = prepareData(user)

      // TODO: best aprox to this?
      // const referredBen = user.referred_benefits
      // const referrerBen = user.referrer_benefits
      // const processedUses = totalWalletAmountUsed / (referredBen.filter((rb: ReferralBenefits) => rb.processed_promo !== null).length || 1)
      // const invitedUses = totalWalletAmountUsed / ((referredBen.filter((rb: ReferralBenefits) => rb.referrer_user_id !== 0 && rb.referred_user_id === user.id).length) + ((referrerBen.filter((rb: ReferralBenefits) => rb.referrer_user_id !== 0 && rb.referrer_user_id === user.id).length)) || 1)

      const totalAtypical = Number(user.orders?.filter((o: Order) => o.status_id && o.status_id > ORDER_STATUS_ID_PENDING_PAYMENT).reduce((acc: any, curr: any) => acc + (curr.atypical || 0), 0) || 0)
      const aliveOrders = user.orders?.filter((o: Order) => o.status_id && ![ORDER_STATUS_ID_REQUESTED, PROCESSED_STATUS, SUPPLEMENT_STATUS, ADMITTED_STATUS, PREPARED_STATUS, SHIPPED_STATUS, DELIVERED_STATUS, PARCIAL_RETURN_STATUS].includes(o.status_id)).length || 0

      return {
        [t('users.id')]: user?.id,
        [t('users.email')]: user?.email,
        [t('users.name')]: user?.name || '',
        [t('users.address_full_name')]: `${user?.default_shipping_address_id?.name || ''} ${user?.default_shipping_address_id?.surnames || ''}`,
        [t('users.address_nif')]: user?.default_shipping_address_id?.nif || '',
        [t('users.created_at')]: formatDateTime(user?.created_at) || '',
        [t('users.role')]: user?.role?.name || 'Basic User',
        [t('users.orders')]: user?.orders?.filter((o: any) => o.status_id !== ORDER_STATUS_ID_PENDING_PAYMENT).length || 0,
        [t('users.ordersInProgress')]: user.orders?.filter((o: any) => o.status_id && [ORDER_STATUS_ID_REQUESTED, PROCESSED_STATUS, SUPPLEMENT_STATUS, ADMITTED_STATUS, PREPARED_STATUS].includes(o.status_id)).length,
        [t('users.delivered_orders')]: user?.orders && user?.orders.length && user.orders?.filter((o: Order) => o.status_id === DELIVERED_STATUS).length | 0,
        [t('users.cancelled_orders')]: user?.orders && user?.orders.length && user.orders?.filter((o: Order) => o.status_id === CANCELLED_STATUS).length | 0,
        [t('users.totalReturns')]: user?.orders?.filter((o: any) => o.status_id === RETURN_STATUS).length || 0,
        [t('users.partialReturns')]: user?.orders?.filter((o: any) => o.status_id === PARCIAL_RETURN_STATUS).length || 0,
        [t('users.decreaseOrders')]: user?.orders?.filter((o: any) => o.status_id === DECREASE_RETURN_STATUS).length || 0,
        [t('users.internalExpenses')]: user?.orders?.filter((o: any) => o.status_id === INTERNAL_SPENDING_RETURN_STATUS).length || 0,
        [t('users.refund_orders')]: user?.orders && user?.orders.length && user.orders?.filter((o: Order) => [REFUND_STATUS].includes(o.status_id!)).length | 0,
        [t('users.penalization_orders')]: user?.orders && user?.orders.length && user.orders?.filter((o: Order) => o.status_id === PENALIZATION_STATUS).length | 0,
        [t('order.pendingPaymentOrders')]: user?.orders && user?.orders.length && user.orders?.filter((o: Order) => o.status_id === ORDER_STATUS_ID_PENDING_PAYMENT).length | 0,
        [t('users.alive_orders')]: aliveOrders,
        [t('users.total')]: user?.orders?.filter((ord: any) => ord.status_id && ![ORDER_STATUS_ID_PENDING_PAYMENT, CANCELLED_STATUS, RETURN_STATUS].includes(ord.status_id)).reduce((acc: number, curr: definitions['orders']) => acc + Number(curr.total), 0),
        [t('users.internal_shipment_total')]: internalShipmentTotal,
        [t('users.supplements_total')]: supplementsTotal,
        [t('users.insurance_total')]: insuranceTotal,
        [t('users.custom_orders_total')]: customOrdersTotal,
        [t('users.internal_total')]: internalTotal,
        [t('users.wallet_balance')]: user?.wallet_balance,
        [t('users.total_benefits')]: accumulatedTotal || 0,
        [t('users.total_wallet_amount_used')]: totalWalletAmountUsed || 0,
        [t('users.expired_benefits')]: expiredBenefits || 0,
        [t('users.atypical')]: totalAtypical,
        // [t('users.invite_uses')]: invitedUses,
        // [t('users.processed_uses')]: processedUses,
        [t('users.last_order_date')]: sortedOrders && sortedOrders.length ? getLastOrder(sortedOrders, -1) : '',
        [t('users.prev_last_order_date')]: sortedOrders && sortedOrders.length ? getLastOrder(sortedOrders, -2) : ''
      }
    })
  }

  const exportEmails = async () => {
    setIsExporting(true)
    setExportProgress(0)

    try {
      const filtersToUse = (filters as any) || []
      const data = await getAllUsers((progress) => {
        setExportProgress(progress)
      }, filtersToUse)

      const result = dataToExport(data as any[])

      setIsExporting(false)
      return result
    } catch (error) {
      setIsExporting(false)
      throw error
    }
  }

  return (
    <List
      headerProps={{
        // extra: <ExportToExcelButton filename='Emails' jsonData={dataToExport(tableProps.dataSource as any[])} />
        extra: <>
          <CreateButton onClick={() => createUserShow()}>{t('users.actions.addUser')}</CreateButton>
          <ExportUsersWithoutOrdersButton filename='Users_without_orders' />
          {/* <ExportAllUsersByDateButton filename='Users_by_date' /> */}
          <ExportToExcelButton filename='Emails' asyncFunction={() => exportEmails()} exportProgress={exportProgress} isExporting={isExporting} />
        </>
      }}
    >
      <Form {...formProps}>
        <Table
          {...tableProps}
          rowKey="id"
          rowClassName={(record) => {
            const { totalWalletAmountUsed, accumulatedTotal } = prepareData(record as any)

            if (totalWalletAmountUsed > accumulatedTotal) {
              return 'user-warning'
            } else {
              return 'user-normal'
            }
          }}
          onRow={(record) => ({
            onClick: (event: any) => {
              if (event.target.classList.contains('role-id') || event.target.closest('.role-id')) {
                setEditId && setEditId(record.id)
              }
            }
          })}
        >
          <Table.Column
            key="id"
            dataIndex="id"
            title="ID"
            sorter
            defaultSortOrder={getDefaultSortOrder('id', sorter)}
          />
          <Table.Column<definitions['users']>
            key="id"
            dataIndex="name"
            title={t('users.name')}
            sorter
            filterDropdown={(props: FilterDropdownProps) => (
              <SelectFilterDropdown
                props={props}
                entity='users'
                optionLabel='name'
                optionValue='id'
                filters={[{
                  field: 'deleted',
                  operator: 'eq',
                  value: false
                }]}
              />
            )}
          />
          <Table.Column<definitions['users']>
            key="address-full-name"
            dataIndex="default_shipping_address_id"
            title={t('users.address_full_name')}
            sorter
            render={(value) => `${value?.name || ''} ${value?.surnames || ''}`}
          />
          <Table.Column<definitions['users']>
            key="address-nif"
            dataIndex="default_shipping_address_id"
            title={t('users.address_nif')}
            sorter
            render={(value) => value?.nif || ''}
          />
          <Table.Column<definitions['users']>
            key="id"
            dataIndex="email"
            title={t('users.email')}
            sorter
            filterDropdown={(props: FilterDropdownProps) => (
              <SelectFilterDropdown
                props={props}
                entity='users'
                optionLabel='email'
                optionValue='id'
                filters={[{
                  field: 'deleted',
                  operator: 'eq',
                  value: false
                }]}
              />
            )}
          />
          <Table.Column<definitions['users']>
            key="phone_number"
            dataIndex={['addresses', 'phone_number']}
            title={t('users.phone_number')}
            sorter
            render={(value) => value || ''}
            filterDropdown={(props) => (
              <FilterDropdown {...props}
                setSelectedKeys={setFilterPhoneNumber}
                clearFilters={async () => {
                  if (filters) {
                    setFilters([...filters?.filter((x: any) => x.field !== 'addresses.phone_number')])
                    setFilterPhoneNumber(undefined)
                  }
                }}
                confirm={() => {
                  if (filters) {
                    if (filters?.find((x: any) => x.field === 'addresses.phone_number')) {
                      const updatedFilters = [...filters]
                      const index = updatedFilters.findIndex((x: any) => x.field === 'addresses.phone_number')
                      updatedFilters[index].value = filterPhoneNumber

                      setFilters([...updatedFilters])
                    } else {
                      setFilters([...filters,
                        { field: 'addresses.phone_number', operator: 'contains', value: filterPhoneNumber }
                      ])
                    }
                  }
                }}
              >
                <Select
                  style={{ minWidth: 200 }}
                  {...selectPhoneProps}
                />
              </FilterDropdown>
            )}
          />
          <Table.Column<definitions['users']>
            key='created_at'
            dataIndex="created_at"
            align="center"
            title={t('users.created_at')}
            render={(value) => <DateField format={DEFAULT_DATE_TIME_FORMAT} value={value}/>}
            sorter
            filterDropdown={() => (
              <DateFilterDropdown
                field="created_at"
                filters={filters}
                setFilters={setFilters}
              />
            )}
          />
          <Table.Column<definitions['users']>
            key='lastAccess'
            dataIndex="last_access"
            align="center"
            title={t('users.lastAccess')}
            render={(value: any) => (value) ? <DateField format={DEFAULT_DATE_TIME_FORMAT} value={value} /> : null}
          />
          <Table.Column<definitions['users']>
            dataIndex='role_id'
            key="role_id"
            title={t('users.role')}
            className='role-id'
            render={(value, data: definitions['users']) => {
              if (isEditing(data.id)) {
                return (
                  <Form.Item
                    name='role_id'
                    style={{ margin: 0 }}
                  >
                    <Select options={rolesOptions} />
                  </Form.Item>
                )
              }
              return rolesOptions.filter(role => role.value === value)[0]?.label
            }}
            sorter
            filterDropdown={(props: FilterDropdownProps) => (
              <SelectFilterDropdown
                props={props}
                entity='roles'
                optionLabel='name'
                optionValue='id'
                sorters={[{
                  field: 'id',
                  order: 'asc'
                }]}
              />
            )}
          />
          <Table.Column<definitions['users']>
            key='users.count'
            dataIndex="orders"
            align="center"
            title={t('users.orders')}
            render={(value: definitions['orders'][]) => value.filter((ord) => ord.status_id && ![ORDER_STATUS_ID_PENDING_PAYMENT, CANCELLED_STATUS, RETURN_STATUS].includes(ord.status_id)).length}
          />
          <Table.Column<definitions['users']>
            key='orders-total'
            dataIndex="orders"
            align="center"
            title={t('users.total')}
            render={(value: definitions['orders'][]) => currency(value.filter((ord) => ord.status_id && ![ORDER_STATUS_ID_PENDING_PAYMENT, CANCELLED_STATUS, RETURN_STATUS].includes(ord.status_id)).reduce((acc, curr) => acc + Number(curr.total), 0))}
          />
          <Table.Column<definitions['users']>
            key='wallet_balance'
            dataIndex="wallet_balance"
            align="center"
            title={t('users.wallet_balance')}
            render={(value: number) => currency(value)}
          />
          <Table.Column<definitions['users']>
            key='accumulatedTotal'
            // dataIndex="accumulatedTotal"
            align="center"
            title={t('users.total_benefits')}
            render={(_value: any, record: any) => currency(prepareData(record).accumulatedTotal)}
          />
          <Table.Column<definitions['users']>
            key='totalWalletAmountUsed'
            // dataIndex="accumulatedTotal"
            align="center"
            title={t('users.total_wallet_amount_used')}
            render={(_value: any, record: any) => currency(prepareData(record).totalWalletAmountUsed)}
          />
          <Table.Column<definitions['users']>
            key='expiredBenefits'
            // dataIndex="accumulatedTotal"
            align="center"
            title={t('users.expired_benefits')}
            render={(_value: any, record: any) => currency(prepareData(record).expiredBenefits)}
          />
          <Table.Column<definitions['users']>
            key='last-order-date'
            dataIndex="orders"
            align="center"
            title={t('users.last_order_date')}
            render={(value: definitions['orders'][]) => value && value.length ? getLastOrder(value as any, -2) : null}
          />
          <Table.Column<definitions['users']>
            key='prev-last-order-date'
            dataIndex="orders"
            align="center"
            title={t('users.prev_last_order_date')}
            render={(value: definitions['orders'][]) => value && value.length ? getLastOrder(value as any, -2) : null}
          />
          <Table.Column<User>
            title={t('table.actions')}
            dataIndex="actions"
            render={(_, record) => {
              if (isEditing(record.id)) {
                return (
                  <Space>
                    <SaveButton
                      {...saveButtonProps}
                      hideText
                      size="small"
                    />
                    <Button
                      {...cancelButtonProps}
                      size="small"
                    >
                      {t('buttons.cancel')}
                    </Button>
                  </Space>
                )
              }

              return (
                <Space>
                  <ButtonLogicDelete click={() => {
                    show()
                    setId(record.id)
                  }} />
                  <DeleteModal
                    modalProps={modalProps}
                    close={close}
                    id={id}
                    entity='users'
                    tableQueryResult={tableQueryResult}
                  />
                  <AddAmountToWalletModal user={record} tableQueryResult={tableQueryResult} />
                  <ReferralBenefitsHistoryModal user={record} tableQueryResult={tableQueryResult} />
                  <UserOrdersModal orders={record.orders} tableQueryResult={tableQueryResult} />
                  <MagicLinkButton email={record.email} />
                </Space>
              )
            }}
          />
        </Table>
        <CreateUserButton
          drawerProps={createDrawerProps}
          formProps={createFormProps}
          closeDrawer={createUserClose}
          tableQueryResult={tableQueryResult}
        />
      </Form>

    </List>
  )
}
